import React, { useState } from 'react';
import * as Icon from 'react-bootstrap-icons';

import { useTranslation } from '../../../common/hooks/useTranslation';
import Button from '../../../common/components/Button/Button';
import { usePokCore } from '../../../common/hooks/usePokCore';
import { AttachmentObjectType } from '../../../common/pokCore/validation/enums';
import { responseErrors } from '../../../common/pokCore/validation/responseErrors';
import {
  AttachmentFileContent,
  ResponseError,
} from '../../../common/pokCore/autogenerated/pokApiClient';

import { EmployeeSignatureEditor } from './EmployeeSignatureEditor';

interface SignButtonProps {
  employeeId: string;
  employeeName: string;
  sign?: boolean;
  refresh: boolean;
  setRefresh: (refresh: boolean) => void;
}

export const SignButton: React.FC<SignButtonProps> = props => {
  const pok = usePokCore();
  const [attachment, setAttachment] = useState<AttachmentFileContent | null>();
  const [show, setShow] = useState(false);
  const { t, tk } = useTranslation('administrationLng');

  const addEditSign = async (
    employeeSign?: Buffer,
    attachmentId?: string,
    name?: string,
  ) => {
    try {
      await pok.attachments.createUpdateAttachmentSign(
        {
          objectId: props.employeeId,
          enObjectType: AttachmentObjectType.EmployeeSign,
          fileContent: employeeSign,
          name: name,
        },
        attachmentId,
      );
      props.setRefresh(!props.refresh);
      setShow(false);
      return {
        saved: true,
        errors: [],
      };
    } catch (response) {
      return {
        saved: false,
        errors: await responseErrors(response as ResponseError),
      };
    }
  };

  const deleteSign = async (attachmentId: string) => {
    try {
      await pok.attachments.delete(attachmentId);
      props.setRefresh(!props.refresh);
      setShow(false);
      return {
        saved: true,
        errors: [],
      };
    } catch (response) {
      return {
        saved: false,
        errors: await responseErrors(response as ResponseError),
      };
    }
  };

  const handleClose = () => {
    setShow(false);
  };

  const handleAddSignature = async () => {
    if (props.employeeId) {
      const attachments = await pok.attachments.getAllForObject(
        AttachmentObjectType.EmployeeSign,
        props.employeeId,
      );
      if (attachments.length > 0) {
        const att = await pok.attachments.getFileByObjectIdAndObjectType(
          props.employeeId,
          AttachmentObjectType.EmployeeSign,
        );

        setAttachment(att);
        setShow(true);
      } else {
        setAttachment(null);
        setShow(true);
      }
    }
  };

  return (
    <>
      {props.sign ? t(tk.common.yesUpperCase) : ''}
      <Button className="px-2" variant="btn-info" onClick={handleAddSignature}>
        {props.sign ? (
          <Icon.PenFill className="icon-blue" size={16} />
        ) : (
          <Icon.Pen className="icon-blue" size={16} />
        )}
      </Button>
      <EmployeeSignatureEditor
        employeeName={props.employeeName}
        attachmentId={attachment?.id}
        employeeSign={attachment?.buffer as Buffer}
        signFilename={attachment?.name}
        onCompleted={addEditSign}
        onDeleted={deleteSign}
        show={show}
        handleClose={handleClose}
      />
    </>
  );
};
